import { Link, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useFetchUnits } from "../hooks/UnitHooks";
import { UnitDTO } from "../types/unitDTO";
import { StationDTO } from "../types/stationDTO";
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from "react";
import { useFetchStations } from "../hooks/StationHooks";

type UnitListArgs = {
    selectedStationId: string;    
    selectedStationIdChanged: (selectedStationId: string) => void;
};

const UnitList = ({ selectedStationId, selectedStationIdChanged }: UnitListArgs) => {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const { organisationId } = useParams<{ organisationId: string }>();
  if (!organisationId) throw new Error("Missing organisationId from params");
  const { data, isPending, isError } = useFetchUnits(organisationId);
  const [sortField, setSortField] = useState<keyof UnitDTO>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [stations, setStations] = useState<StationDTO[]>([]);
  const { data: stationsData, isLoading: stationsIsLoading, isError: stationsIsError } = useFetchStations(organisationId);


    useEffect(() => {
        if (stationsData)
        {            
            setStations(stationsData);

            if (selectedStationId != "all")
            {                
                const stationExists = stationsData.some(station => station.id === selectedStationId);
                if (!stationExists) {
                    selectedStationIdChanged("all"); // Reset to "all" if the selected station is not found
                }
            }
        }
    }, [stationsData]);


    useEffect(() => {
        if (isError || stationsIsError) { nav('/login') }

    }, [isError, stationsIsError]);

  const handleSort = (field: keyof UnitDTO) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortData = (data: UnitDTO[]) => {
    return [...data].sort((a, b) => {
      if (sortField === 'name') {
        return sortDirection === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
        }

        if (sortField === 'station') {
            return sortDirection === 'asc'
                ? a.station.name.localeCompare(b.station.name)
                : b.station.name.localeCompare(a.station.name);
        }

      if (sortField === 'validFrom' || sortField === 'validTo' || sortField === 'lastModified') {
        const aValue = new Date(a[sortField]).getTime();
        const bValue = new Date(b[sortField]).getTime();
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  };

    const filteredData = data?.filter(unit =>
        (unit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        unit.station.name.toLowerCase().includes(searchTerm.toLowerCase()) ) &&
        (selectedStationId === "all" || unit.station.id === selectedStationId)
    );

  
  if (isPending || stationsIsLoading) return <h2>Hold on</h2>;

  return (
    <div>
      <div className="row mb-2 mt-2">
        <h5 className="subtitle text-left">
          {t('unit.title')}
        </h5>
      </div>

      <Link className="btn btn-primary mt-2 mb-3" to={`/organisation/${organisationId}/unit/add`}>
        {t('unit.add')}
      </Link>

          <div className="row mb-3">
              <div className="col-md-4">
                  <input
                      type="text"
                      className="form-control"
                      placeholder={t('common.search')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                  />
              </div>
              <div className="col-md-4">
                  <select
                      className="form-select"
                      value={selectedStationId}
                      onChange={(e) => {
                          selectedStationIdChanged(e.target.value);
                      }}
                  >
                      <option value="all">{t('common.fromAllStations')}</option>                    
                      {stations?.map((station) => (
                          <option key={station.id} value={station.id}>
                              {station.name}
                          </option>
                      ))}
                  </select>
              </div>
          </div>





    <table className="table table-hover table-striped"
        style={
            {
                "--bs-table-hover-bg": "#C9DDED"
            } as React.CSSProperties
        }
        >
       <thead>
          <tr>
            <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
              {t('common.name')} {sortField === 'name' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('station')} style={{ cursor: 'pointer' }}>
                {t('unit.station')} {sortField === 'station' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('validFrom')} style={{ cursor: 'pointer' }}>
              {t('common.validFrom')} {sortField === 'validFrom' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('validTo')} style={{ cursor: 'pointer' }}>
              {t('common.validTo')} {sortField === 'validTo' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('lastModified')} style={{ cursor: 'pointer' }}>
              {t('common.lastModified')} {sortField === 'lastModified' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredData && sortData(filteredData).map((unit: UnitDTO) => {
              const dateOptions: Intl.DateTimeFormatOptions = {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              };

              return (
                  <tr key={unit.id} style={{ cursor: 'pointer' }} onClick={() => nav(`/organisation/${organisationId}/unit/${unit.id}`)}>
                  <td>{unit.name}</td>
                  <td>{unit.station.name}</td>
                  <td>{new Date(unit.validFrom + 'Z').toLocaleString(i18n.language === 'fi' ? 'fi-FI' : 'en-US', dateOptions)}</td>
                  <td>{new Date(unit.validTo + 'Z').toLocaleString(i18n.language === 'fi' ? 'fi-FI' : 'en-US', dateOptions)}</td>
                  <td>{new Date(unit.lastModified + 'Z').toLocaleString(i18n.language === 'fi' ? 'fi-FI' : 'en-US', dateOptions)}</td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
};

export default UnitList;
